/* eslint-disable no-console */
import React, { useCallback, useEffect } from 'react';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from 'react-router-dom';
import { Security, LoginCallback, SecureRoute } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  getOktaSettings,
  validateVersion,
  redirect,
} from '../../actions/index';
import Constants from '../../services/Constants';
import ChangePassword from '../change_password';
import Register from '../login/Register';
import ClientHUBTitle from '../ClientHUBTitle';
import Main from '../Main';
import NotFound from '../NotFound';
import PlansRouter from './PlansRouter';
import SkyanRouter from './SkyanRouter';
import MessagesPopup from '../messages/MessagesPopup';
import ConsultingResourcesRouter from './ConsultingResourcesRouter';
import Downloads from '../Downloads';
import VideosRouter from './VideosRouter';
import { oktaAuth } from '../login/config';
import Authenticated from '../Authenticated';
import PreLogin from '../PreLogin';
import Vote from '../Vote';

const redirectPage = (props) => {
  if (props.redirectTo) {
    props.redirect(null);
    return <Redirect push={true} to={props.redirectTo} />;
  }
  return null;
};

export const AppRouter = (props) => {
  useEffect(() => {
    props.getOktaSettings();
    props.validateVersion();
  }, []);

  const withMain = (selectedView, viewParams = {}) => {
    if (
      selectedView !== Constants.TERMS_AND_CONDITIONS_VIEW &&
      props.acceptedTerms === false
    ) {
      return withMain(Constants.TERMS_AND_CONDITIONS_VIEW);
    }
    return (
      <>
        <Main selectedView={selectedView} viewParams={viewParams} />
        <ClientHUBTitle selectedView={selectedView} viewParams={viewParams} />
      </>
    );
  };

  const onProfile = () => {
    return withMain(Constants.PROFILE_VIEW);
  };

  const onTerms = () => {
    return withMain(Constants.TERMS_AND_CONDITIONS_VIEW);
  };

  const onPlans = () => {
    return withMain(Constants.PLANS_VIEW);
  };

  const onAdmin = () => {
    return withMain(Constants.ADMIN_MANAGER);
  };

  const onConsultingResources = (event) => {
    return props.okta.isEmployee ? (
      <ConsultingResourcesRouter
        match={event?.match}
        withMain={withMain}
        isEmployee={props.employee}
      />
    ) : (
      onPlans()
    );
  };

  const onSkyan = ({ match }) => {
    return <SkyanRouter match={match} withMain={withMain} />;
  };

  const onVideos = ({ match }) => {
    return (
      <VideosRouter
        match={match}
        topics={match.params.topics}
        categories="video"
        withMain={withMain}
      />
    );
  };

  const onHUBAdmin = () => {
    if (props.okta.isEmployee) {
      return withMain(Constants.HUB_SETTINGS_VIEW);
    }
    return withMain(Constants.MAINTENANCE_SCREEN);
  };

  const onMaintenanceMode = () => {
    return withMain(Constants.MAINTENANCE_SCREEN);
  };

  const onBlog = ({ match }) => {
    return (
      <VideosRouter
        match={match}
        topics=""
        categories="blog"
        withMain={withMain}
      />
    );
  };

  const onPlan = ({ match }) => {
    return <PlansRouter match={match} withMain={withMain} />;
  };

  const onDownloadDocuments = ({ match }) => {
    return (
      <div>
        <Downloads urlParams={match.params} />
        {onPlans()}
      </div>
    );
  };
  
  const onVote = ({ match }) => {
    return <Vote urlParams={match.params} />;
  };

  const onRegister = () => {
    return <Register />;
  };

  const onChangePassword = () => {
    return <ChangePassword />;
  };

  const onLogout = async () => {
    oktaAuth.signOut();
  };

  const onNotFound = () => {
    return <NotFound />;
  };

  const onPreLogin = () => {
    return <PreLogin />;
  };

  const restoreOriginalUri = useCallback(async (_oktaAuth, originalUri) => {
    window.location.replace(
      toRelativeUrl(originalUri || '/', window.location.origin)
    );
  }, []);
  const { maintenanceMode } = props.oktaSettings;

  if (maintenanceMode) {
    return (
      <Router>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <ClientHUBTitle {...props} viewParams={{}} />
          <Switch>
            <Route path="/login/callback" component={LoginCallback} />
            <SecureRoute path="/settings" render={() => onHUBAdmin()} />
            <SecureRoute path="/logout" render={() => onLogout()} />
            <Route path="/maintenance" render={() => onMaintenanceMode()} />
            <Route>
              <Redirect to="/maintenance" />
            </Route>
          </Switch>
          <Authenticated />
          <MessagesPopup />
        </Security>
      </Router>
    );
  }

  return (
    <>
      <Router>
        {redirectPage(props) || (
          <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <ClientHUBTitle {...props} viewParams={{}} />
            <Switch>
              <Route path="/login/callback" component={LoginCallback} />
              <SecureRoute exact path="/login">
                <Redirect to="/portfolios" />
              </SecureRoute>
              <Route exact path="/register" render={() => onRegister()} />
              <Route path="/terms" render={onTerms} />
              <SecureRoute exact path="/logout" render={() => onLogout()} />
              <SecureRoute
                exact
                path="/changePassword"
                render={() => onChangePassword()}
              />
              <SecureRoute
                path="/portfolios/Download/:fileId/:documentTittle"
                render={(props) => onDownloadDocuments(props)}
              />
              <SecureRoute
                path="/vote/:data"
                render={(props) => onVote(props)}
              />
              <SecureRoute path="/portfolios" render={() => onPlans()} />
              <SecureRoute path="/admin" render={() => onAdmin()} />
              <SecureRoute
                path="/consultingResources"
                render={(evt) => onConsultingResources(evt)}
              />
              <SecureRoute path="/skyan" render={onSkyan} />
              <SecureRoute
                path="/videos/:topics"
                render={(props) => onVideos(props)}
              />
              <SecureRoute path="/blog" render={onBlog} />
              <SecureRoute path="/settings" render={onHUBAdmin} />
              <SecureRoute
                path="/portfolio/:clientLetter/:slugPlanName"
                render={onPlan}
              />
              <SecureRoute path="/profile" render={onProfile} />
              <Route path="/" component={onPreLogin} />
              <Route render={onNotFound} />
            </Switch>
            <Authenticated />
            <MessagesPopup />
          </Security>
        )}
      </Router>
    </>
  );
};

AppRouter.propTypes = {
  oktaSettings: PropTypes.object.isRequired,
  getOktaSettings: PropTypes.func.isRequired,
  validateVersion: PropTypes.func.isRequired,
  redirect: PropTypes.func.isRequired,
  redirectTo: PropTypes.string,
  okta: PropTypes.object.isRequired,
  acceptedTerms: PropTypes.bool,
  employee: PropTypes.bool,
  email: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    okta: state.okta,
    oktaSettings: state.okta.settings,
    redirectTo: state.ui.redirect,
    acceptedTerms: state.terms.accepted,
  };
};

export default connect(mapStateToProps, {
  getOktaSettings,
  validateVersion,
  redirect,
})(AppRouter);
